import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';

import '../styles/login.css';
import { showSuccess, showError, showInfo } from '../utils/toastHelper';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, resendVerification } = useAuth();
  const { branding, loading: brandingLoading, notFound, error: brandingError } = useBranding();
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const from = location.state?.from?.pathname || "/products";
  
  const urlParams = new URLSearchParams(location.search);
  const isTimeout = urlParams.get('timeout') === 'true';

  useEffect(() => {
    if (location.state?.message) {
      setFormSuccess(location.state.message);
      window.history.replaceState({}, document.title);
    }
  }, [location, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFormError('');
    setFormSuccess('');

    try {
      const result = await login(formData.email, formData.password);

      if (result.success) {
        const userRole = result.user?.role;
        // Form success mesajını ve gereksiz bekleme süresini kaldırdık. 
        // Kullanıcı hemen yönlendiriliyor.
        
        if (userRole === 'SUPER_ADMIN' || userRole === 'COMPANY_ADMIN') {
          navigate('/admin', { replace: true });
        } else if (userRole === 'SHIPPING_STAFF') {
          navigate('/admin/orders', { replace: true });
        } else {
          navigate(from, { replace: true });
        }
      } else {
        setFormError(result.message || 'Geçersiz e-posta veya şifre.');
      }
    } catch (error) {
      setFormError('Bağlantı hatası. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!formData.email) return;
    setResending(true);
    
    // We show a loading toast for premium feel
    const toastId = showInfo('Doğrulama maili gönderiliyor...');
    
    const result = await resendVerification(formData.email);
    
    if (result.success) {
      showSuccess(result.message || 'Doğrulama maili gönderildi.');
    } else {
      showError(result.message || 'Mail gönderilirken bir hata oluştu.');
    }
    
    setResending(false);
  };

  const companyName = branding.name || '';
  const logoSrc = branding.logoUrl || branding.companyLogo || null;
  // brandColor: şirket ayarlarından yönetilen login panel rengi (email şablonu primaryColor'undan bağımsız)
  const accent = branding.brandColor || '#1e293b';
  const accentSoft = accent + '18';
  const heroTitle = branding.heroTitle || '';
  const heroSub = branding.heroSubtitle || branding.tagline || '';

  if (brandingLoading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: '#f8f9fc' }}>
        <div className="spinner-border" style={{ color: '#aaa' }} role="status" />
      </div>
    );
  }

  if (brandingError) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: '#f8f9fc' }}>
        <div className="text-center px-4" style={{ maxWidth: 420 }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>⚠️</div>
          <h2 className="fw-bold mb-2" style={{ color: '#1a1a2e' }}>Bağlantı Hatası</h2>
          <p className="text-muted mb-3">
            Sunucuya bağlanılamadı. Lütfen daha sonra tekrar deneyin.
          </p>
          {brandingError && (
            <p className="text-muted mb-3" style={{ fontSize: 12 }}>
              Detay: {brandingError}
            </p>
          )}
          <button className="btn btn-outline-secondary btn-sm" onClick={() => window.location.reload()}>
            Tekrar Dene
          </button>
        </div>
      </div>
    );
  }

  // Şirket bulunamadığında (notFound = true) engelleyici bir sayfa göstermiyoruz.
  // Bunun yerine jenerik (şirketsiz) login formu render edilecek.

  return (
    <div className="login-page">
      {/* ── Left: Branded Panel ─────────────────────────────── */}
      <div className="login-brand-panel" style={{ '--accent': accent, '--accent-soft': accentSoft }}>
        <div className="login-brand-content">
          {logoSrc && (
            <img src={logoSrc} alt={companyName || 'Logo'} className="login-brand-logo" />
          )}
          {heroTitle && <h1 className="login-brand-title">{heroTitle}</h1>}
          {heroSub && <p className="login-brand-subtitle">{heroSub}</p>}
          {!logoSrc && !heroTitle && companyName && companyName !== 'System Admin Company' && (
            <h1 className="login-brand-title">{companyName}</h1>
          )}
        </div>
      </div>

      {/* ── Right: Form ─────────────────────────────────────── */}
      <div className="login-form-panel">
        <div className="login-form-wrapper">
          {/* Mobile-only header */}
          <div className="login-mobile-header" style={{ '--accent': accent }}>
            {logoSrc ? (
              <img src={logoSrc} alt={companyName || 'Logo'} className="login-mobile-logo" />
            ) : companyName && companyName !== 'System Admin Company' ? (
              <span className="login-mobile-name" style={{ color: accent }}>{companyName}</span>
            ) : null}
          </div>

          <div className="login-form-inner">
            <h2 className="login-form-title">Giriş Yap</h2>
            <p className="login-form-desc">
              Hesabınıza erişmek için bilgilerinizi girin.
            </p>

            {formError && (
              <div className="alert alert-danger mb-4" role="alert" style={{ fontSize: '0.875rem', borderRadius: '10px', backgroundColor: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca' }}>
                <div className="d-flex align-items-center mb-2">
                  <i className="fas fa-exclamation-circle me-2" style={{ fontSize: '1.2rem' }} />
                  <div>{formError}</div>
                </div>
                {formError.toLowerCase().includes('doğrulayın') && (
                  <button 
                    type="button" 
                    className="btn btn-sm btn-outline-danger w-100 mt-1 d-flex justify-content-center align-items-center"
                    onClick={handleResend}
                    disabled={resending}
                    style={{ fontWeight: 600 }}
                  >
                    {resending ? (
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    ) : (
                      <i className="fas fa-paper-plane me-2" />
                    )}
                    Tekrar Gönder
                  </button>
                )}
              </div>
            )}

            {formSuccess && (
              <div className="alert alert-success d-flex align-items-center mb-4" role="alert" style={{ fontSize: '0.875rem', borderRadius: '10px', backgroundColor: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0' }}>
                <i className="fas fa-check-circle me-2" style={{ fontSize: '1.2rem' }} />
                <div>{formSuccess}</div>
              </div>
            )}

            {isTimeout && !formError && !formSuccess && (
              <div className="alert alert-warning d-flex align-items-center mb-4" role="alert" style={{ fontSize: '0.875rem', borderRadius: '10px' }}>
                <i className="fas fa-shield-alt me-2" style={{ fontSize: '1.2rem' }} />
                <div>
                  Oturumunuz işlem yapılmadığı için güvenlik amacıyla sonlandırıldı. Lütfen tekrar giriş yapın.
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="login-form">
              <div className="login-field">
                <label className="login-label" htmlFor="login-email">E-posta</label>
                <div className="login-input-wrap" style={{ '--accent': accent }}>
                  <i className="fas fa-envelope login-input-icon" />
                  <input
                    id="login-email"
                    type="email"
                    name="email"
                    className="login-input"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    autoComplete="email"
                    disabled={loading}
                    placeholder="ornek@mail.com"
                  />
                </div>
              </div>

              <div className="login-field">
                <label className="login-label" htmlFor="login-password">Şifre</label>
                <div className="login-input-wrap" style={{ '--accent': accent }}>
                  <i className="fas fa-lock login-input-icon" />
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    className="login-input"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    autoComplete="current-password"
                    disabled={loading}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    className="login-eye-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                    aria-label={showPassword ? 'Şifreyi gizle' : 'Şifreyi göster'}
                  >
                    <i className={`fas fa-${showPassword ? 'eye-slash' : 'eye'}`} />
                  </button>
                </div>
              </div>

              <div className="d-flex justify-content-end mb-1">
                <Link to="/forgot-password" className="login-link" style={{ color: accent }}>
                  Şifremi Unuttum?
                </Link>
              </div>

              <button
                type="submit"
                className="login-submit"
                style={{ '--accent': accent }}
                disabled={loading}
              >
                {loading ? (
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                ) : (
                  <i className="fas fa-arrow-right me-2" />
                )}
                Giriş Yap
              </button>
            </form>

            <p className="login-signup-text">
              Hesabınız yok mu?{' '}
              <Link to="/signup" className="login-link fw-semibold" style={{ color: accent }}>Kayıt Olun</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
